import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getShipments } from '@wove/api';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Spinner,
  Card,
  CardHeader,
  CardContent,
} from '@wove/web-ui';
import { format } from 'date-fns';
import { ChevronRight, Search, Filter, Download, Plus } from 'lucide-react';

const ShipmentsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['shipments', searchTerm, statusFilter, modeFilter, page],
    queryFn: () => getShipments({
      search: searchTerm || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      mode: modeFilter === 'all' ? undefined : modeFilter,
      page,
      pageSize,
    }),
  });

  const handleRowClick = (shipmentId: string) => {
    navigate(`/shipments/${shipmentId}`);
  };

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, "warning" | "info" | "success" | "error" | "secondary"> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'error',
    };
    
    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getShipmentTypeIcon = (type?: string) => {
    const icons = {
      ocean: '🚢',
      air: '✈️',
      ground: '🚚',
      rail: '🚂',
    };
    return icons[type as keyof typeof icons] || '📦';
  };

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="text-center py-8 text-red-500">
            Error loading shipments: {error.message}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Shipments</h1>
        <Button
          onClick={() => navigate('/shipments/new')}
          leftIcon={<Plus size={16} />}
        >
          New Shipment
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by commodity, reference numbers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="ocean">Ocean</SelectItem>
                <SelectItem value="air">Air</SelectItem>
                <SelectItem value="ground">Ground</SelectItem>
                <SelectItem value="rail">Rail</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="secondary" leftIcon={<Download size={16} />}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              <Table>
                <thead>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Commodity</TableHead>
                    <TableHead>Origin</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Documents</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </thead>
                <TableBody>
                  {data?.data.map((shipment) => (
                    <TableRow
                      key={shipment.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleRowClick(shipment.id)}
                    >
                      <TableCell className="font-mono text-sm">
                        {shipment.id.slice(-8)}
                      </TableCell>
                      <TableCell>{getStatusBadge(shipment.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{getShipmentTypeIcon(shipment.shipmentType)}</span>
                          <span className="capitalize">
                            {shipment.shipmentType || 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{shipment.commodity || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {shipment.placeOfReceiptPort || shipment.portOfLoading || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {shipment.placeOfDeliveryPort || shipment.portOfDischarge || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {shipment.documentCount || 0} docs
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {format(new Date(shipment.createdAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <ChevronRight size={16} className="text-gray-400" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data?.data.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No shipments found. Try adjusting your filters or create a new shipment.
                </div>
              )}

              {data && data.pagination.total > pageSize && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-500">
                    Showing {(page - 1) * pageSize + 1} to{' '}
                    {Math.min(page * pageSize, data.pagination.total)} of{' '}
                    {data.pagination.total} shipments
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page * pageSize >= data.pagination.total}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShipmentsList;